import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * Размеры совпадают с Button: 'md' — тулбарная кнопка (h-9, text-sm),
 * 'sm' — кнопка действия в шапке страницы (text-xs), чтобы выпадающая кнопка
 * стояла в один рост с обычными рядом.
 */
type DropdownButtonSize = 'sm' | 'md';

type DropdownPanelPosition = {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
};

const sizeStyles: Record<DropdownButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'h-9 px-3 text-sm',
};

interface DropdownButtonProps {
  label?: React.ReactNode;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  active?: boolean;
  size?: DropdownButtonSize;
  /** Render-prop receives a `close` callback so menu items can dismiss */
  children: (close: () => void) => React.ReactNode;
  align?: 'left' | 'right';
  direction?: 'down' | 'up';
  className?: string;
  buttonClassName?: string;
  panelClassName?: string;
  disabled?: boolean;
  showChevron?: boolean;
  title?: string;
  /** Extra attributes (e.g. data-tour) spread onto the trigger button */
  buttonAttrs?: Record<string, string>;
  /** Render the panel in document.body so overflow containers cannot clip it. */
  portal?: boolean;
}

/**
 * Reusable dropdown button — toolbar style with click-outside dismiss.
 * Generalized from features/products/components/filters/FilterDropdownButton.
 */
export const DropdownButton: React.FC<DropdownButtonProps> = ({
  label,
  icon,
  badge,
  active,
  size = 'md',
  children,
  align = 'left',
  direction = 'down',
  className,
  buttonClassName,
  panelClassName,
  disabled,
  showChevron = true,
  title,
  buttonAttrs,
  portal = false,
}) => {
  const [open, setOpen] = useState(false);
  const [panelPosition, setPanelPosition] = useState<DropdownPanelPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = React.useId();

  useEffect(() => {
    if (!open || (portal && !panelPosition)) return;
    panelRef.current?.querySelector<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), [tabindex="0"]')?.focus();
    // Position changes while scrolling must not steal focus from a chosen menu item.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, portal, Boolean(panelPosition)]);

  const updatePanelPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const placeAbove = direction === 'up'
      ? spaceAbove >= 96 || spaceAbove >= spaceBelow
      : spaceBelow < 180 && spaceAbove > spaceBelow;
    const available = Math.max(96, placeAbove ? spaceAbove - 4 : spaceBelow - 4);
    const horizontal = align === 'right'
      ? { right: Math.min(Math.max(8, viewportWidth - rect.right), Math.max(8, viewportWidth - 228)) }
      : { left: Math.min(Math.max(8, rect.left), Math.max(8, viewportWidth - 228)) };

    setPanelPosition({
      ...horizontal,
      maxHeight: available,
      ...(placeAbove ? { bottom: viewportHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    });
  }, [align, direction]);

  useEffect(() => {
    if (!open || !portal) {
      setPanelPosition(null);
      return;
    }

    updatePanelPosition();
    const onScrollOrResize = () => updatePanelPosition();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, portal, updatePanelPosition]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  const panel = open && (!portal || panelPosition) ? (
    <div
      ref={panelRef}
      id={panelId}
      role="menu"
      className={cn(
        portal ? 'fixed z-[70] max-w-[calc(100vw-1rem)] overflow-y-auto' : 'absolute z-40',
        'min-w-[220px] rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-zinc-800 dark:bg-zinc-900',
        !portal && (direction === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'),
        !portal && (align === 'right' ? 'right-0' : 'left-0'),
        panelClassName,
      )}
      style={portal && panelPosition ? {
        left: panelPosition.left,
        right: panelPosition.right,
        top: panelPosition.top,
        bottom: panelPosition.bottom,
        maxHeight: panelPosition.maxHeight,
      } : undefined}
    >
      {children(() => { setOpen(false); triggerRef.current?.focus(); })}
    </div>
  ) : null;

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        title={title}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        {...(buttonAttrs || {})}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1.5 border rounded-md bg-white text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800/60',
          sizeStyles[size],
          active ? 'border-indigo-400 ring-1 ring-indigo-200 dark:border-indigo-500 dark:ring-indigo-900' : 'border-gray-300 dark:border-zinc-700',
          buttonClassName,
        )}
      >
        {icon && <span className="shrink-0">{icon}</span>}
        {label && <span>{label}</span>}
        {badge}
        {showChevron && (
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 text-gray-400 dark:text-zinc-500 transition-transform',
              open && 'rotate-180',
            )}
          />
        )}
      </button>
      {portal ? (panel && createPortal(panel, document.body)) : panel}
    </div>
  );
};

export default DropdownButton;
